import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { DraftLineOptions, UnitsPreference } from "@heraion/shared";

import { weightUnitLabels } from "../weightUnitLabels";

/** The words the shared Draft line formatter needs (previewFormat.ts), in the
 *  athlete's language. Values read in the draft's own unit: the same unit the
 *  set table's fields are typed in, so the line and the field agree. */
export function useDraftLineOptions(cellUnit: UnitsPreference): DraftLineOptions {
  const { t, i18n } = useTranslation();
  return useMemo(
    () => ({
      cellUnit,
      displayUnit: cellUnit,
      locale: i18n.language,
      labels: {
        units: weightUnitLabels(t),
        seconds: t("editorV3.secondsUnit", { defaultValue: "s" }),
        history: t("editorV3.historyLoad", { defaultValue: "last time" }),
        setRange: (lo: number, hi: number) =>
          t("editorV3.setRangeA11y", { lo, hi, defaultValue: "{{lo}} to {{hi}} sets of" }),
        // «100 kg ·» holds together (U+00A0, the load's own space too); a
        // tight cell breaks after the «·» only.
        ofMax: (load: string, pct: string) =>
          t("editorV3.grid.effortOfMax", {
            load: load.replace(/ /g, "\u00a0"),
            pct,
            defaultValue: "{{load}} · ≈{{pct}}%",
          }),
      },
    }),
    [cellUnit, i18n.language, t],
  );
}
